import { useCallback, useEffect } from 'react';
import type { RefObject } from 'react';

type UseOnClickOutsideProps = {
  /**
   * Element ref, which you can get via React.useRef, and then pass that ref as ref="" on your element.
   */
  ref: RefObject<HTMLElement>;
  /**
   * Called when a click happens outside of the element.
   */
  handler: (event: MouseEvent | TouchEvent) => void;
  /**
   * Called when a click happens outside of the element.
   */
  ignoredElements?: Array<RefObject<HTMLElement>>;
  /**
   * If the click outside should be skipped. This can be helpful for cases
   * where you need to temporarily turn off the click outside behaviour,
   * such as when a modal is opened over top of the drawer.
   *
   * If this prop never changes, consider just not using this hook.
   */
  shouldSkipClickOutside?: boolean;
};

/**
 * Hook for handling what should happen if a click happens anywhere outside a specified element.
 * Handy for implementing things like modals, where we want to close the modal if a user clicks on anything outside the modal.
 *
 * @param {(event: MouseEvent | TouchEvent) => void} [ignoredElements] - Called when a click happens outside of the element.
 * @example
 * import React, { useRef, useCallback } from 'react';
 * import useOnClickOutside from '../hooks/useOnClickOutside';
 *
 * const MyComponent = ({ onClose }) => {
 *   const ref = useRef();
 *   useOnClickOutside({
 *     ref,
 *     handler: useCallback(onClose),
 *   });
 *   return (
 *     <div>
 *       <div>Clicking me will call onClose</div>
 *       <div ref={ref}>
 *         Clicking me will not call onClose
 *       </div>
 *       <div>Clicking me will call onClose</div>
 *     </div>
 *   );
 * }
 */
const useOnClickOutside = ({
  ref,
  handler,
  ignoredElements,
  shouldSkipClickOutside,
}: UseOnClickOutsideProps): void => {
  //  Check if the element we care about includes the element which was just clicked.
  const handleDocumentClick = useCallback((event: MouseEvent | TouchEvent) => {
    //  Bail if we should skip the click outside.
    if (shouldSkipClickOutside) {
      return;
    }
    //  Do nothing if clicking on something inside the ref.
    if (ref?.current?.contains(event.target as Node)) {
      return;
    }
    //  Do nothing if clicking on the click target which triggers the opposite of the handler.
    //  An example of this would be the button which opens a Drawer, as we don't want to open and close it on te same event.
    if (ref?.current && ignoredElements?.some(clickTarget => clickTarget?.current?.contains(event.target as Node))) {
      return;
    }

    handler(event);
  }, [ref, handler, ignoredElements, shouldSkipClickOutside]);

  useEffect(() => {
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick); // Handle touchscreen devices too.
    //  Remove the handler onUnmount
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
    };
  }, [handleDocumentClick]); // Note: you'll want to wrap handler in a useCallback() to ensure the ref doesn't change on each render.
};

export default useOnClickOutside;
